import React, { useState, useEffect } from 'react';
import i18next from 'core/i18n';
import { DialogContent, SmallTitle, Textarea, useFlashMessage } from '@indigo-ag/component-module';

import { Credit } from '@indigo-ag/schema';

import { EnhancedDialog, createStylesHook, ModalActionButtons } from 'shared';
import { FinancialInformationSectionProps } from '../../FinancialInformationSection';

export interface FinancialInformationModalProps extends FinancialInformationSectionProps {
  closeModal: () => void;
  open: boolean;
  // TODO create type for function
  updateCredit: (newCreditInfo: Partial<Credit>) => Promise<unknown>;
}

const useStyles = createStylesHook({
  financialInfoTitle: {
    marginBottom: '30px !important'
  },
  financialRestricitionTitle: {
    margin: '60px 0 35px 0 !important'
  }
});

export const FinancialInformationModal: React.FunctionComponent<FinancialInformationModalProps> = ({
  closeModal,
  open,
  financialInformationInfo,
  updateCredit
}) => {
  /**
   * State
   */
  const [financialPending, setFinancialPending] = useState(financialInformationInfo?.financialPendency || '');
  const [debtSituation, setDebtSituation] = useState(financialInformationInfo?.debtSituation || '');
  const [moreInformation, setMoreInformation] = useState(financialInformationInfo?.financialInformation || '');

  /**
   * Styles
   */
  const classes = useStyles();

  const onCleanFinancialInformationSpace = () => {
    setFinancialPending(financialInformationInfo?.financialPendency || '');
    setDebtSituation(financialInformationInfo?.debtSituation || '');
    setMoreInformation(financialInformationInfo?.financialInformation || '');
  };

  useEffect(() => {
    onCleanFinancialInformationSpace();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFinancialPending = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setFinancialPending(event.target.value);
  };
  const handleDebtSituation = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setDebtSituation(event.target.value);
  };
  const handleMoreInformation = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setMoreInformation(event.target.value);
  };

  const { addFlashMessage } = useFlashMessage();

  const updateInfo = () => {
    return updateCredit({
      financialPendency: financialPending,
      debtSituation,
      financialInformation: moreInformation
    }).then(() => {
      addFlashMessage({
        content: `${i18next.t('financialInformation')} ${i18next.t('hasBeenUpdatedSuccessfully')}`,
        title: '',
        variant: 'success'
      });
    });
  };

  return (
    <EnhancedDialog open={open} title={i18next.t('financialInformation')} onClose={closeModal}>
      <DialogContent>
        <SmallTitle className={classes.financialInfoTitle}>{i18next.t('financialRestrictions')}</SmallTitle>
        <Textarea
          label={i18next.t('financialPending')}
          value={financialPending}
          onChange={handleFinancialPending}
          resizable
          rows={3}
        />

        <SmallTitle className={classes.financialRestricitionTitle}>{i18next.t('commercialInformation')}</SmallTitle>
        <Textarea
          label={i18next.t('debtSituation')}
          value={debtSituation}
          onChange={handleDebtSituation}
          resizable
          rows={3}
        />
        <Textarea
          label={i18next.t('moreInformation')}
          value={moreInformation}
          onChange={handleMoreInformation}
          resizable
          rows={3}
        />
      </DialogContent>
      <ModalActionButtons
        changeButtonName={i18next.t('saveChanges')}
        closeModal={closeModal}
        disabled={!financialPending || !debtSituation || !moreInformation}
        save={updateInfo}
      />
    </EnhancedDialog>
  );
};
